import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useAxesTooltip } from "@mui/x-charts";
import type { FC } from "react";
import { Trans } from "react-i18next";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ChartTooltipRow } from "web_myplann/Portfolio/Components/Redesign/Detail/ChartTooltipRow";
import { useInterpolatedPeriods } from "web_myplann/Portfolio/Components/Redesign/Detail/InterpolatedPeriodsContext";
import { findInterpolatedPeriod } from "web_myplann/Portfolio/Helpers/interpolatedPeriods";
import { useMouseTouchPosition } from "web_myplann/Shared/Hooks/useMouseTouchPosition";

const TOOLTIP_WIDTH = 180;
const NO_DATA_TOOLTIP_WIDTH = 220;
const VIEWPORT_PADDING = 16;
const TOOLTIP_OFFSET_Y = 100;

export const ChartTooltip: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const pos = useMouseTouchPosition();
	const tooltip = useAxesTooltip();
	const interpolatedPeriods = useInterpolatedPeriods();

	if (!tooltip?.[0]) {
		return null;
	}

	const data = tooltip[0];
	const period = findInterpolatedPeriod(interpolatedPeriods, data.axisValue);
	const width = period ? NO_DATA_TOOLTIP_WIDTH : TOOLTIP_WIDTH;
	const left = Math.min(
		Math.max(pos.x - width / 2, VIEWPORT_PADDING),
		window.innerWidth - (width + VIEWPORT_PADDING),
	);

	return (
		<Stack
			position="absolute"
			left={left}
			top={pos.y - TOOLTIP_OFFSET_Y}
			width={width}
			px={theme.spacingTokens.xs}
			py={theme.spacingTokens.xxs}
			borderRadius={theme.radii.sm}
			bgcolor={theme.palette.grey[100]}
		>
			{period ? (
				<Typography variant="caption" color={theme.palette.grey[900]}>
					<Trans
						i18nKey={getResourcePath(Resources.Portfolio.Chart.NoDataPeriod)}
						values={{ from: period.fromLabel, to: period.toLabel }}
						components={{ b: <Box component="span" fontWeight={700} /> }}
					/>
				</Typography>
			) : (
				<>
					<ChartTooltipRow
						label={t(Resources.Portfolio.Chart.Date)}
						value={data.axisFormattedValue}
					/>
					{data.seriesItems
						.filter(
							({ value }) =>
								value !== null && value !== undefined && value !== 0,
						)
						.map(({ formattedLabel, formattedValue }) => (
							<ChartTooltipRow
								key={formattedLabel}
								label={formattedLabel}
								value={formattedValue}
							/>
						))}
				</>
			)}
		</Stack>
	);
};
